import React from 'react';
import { combineAlertOrders, type CombineOrder, type CombineVariant } from '@/lib/combineShipment';
import CombineShipmentCard from './CombineShipmentCard';
import CombineAlertSheet from './CombineAlertSheet';
import CombineScanOverlay from './CombineScanOverlay';
import type { CombineStep } from './useCombineStep';

export { default as useCombineStep } from './useCombineStep';
export type { CombineStep } from './useCombineStep';
export { default as CombineShipmentCard } from './CombineShipmentCard';

/**
 * The combine step's three surfaces for a Pick or Verify screen: the card (inline, where it is
 * rendered), the one-time alert sheet and the pick-ticket scanner (both portalled over the screen).
 * `showAlert` false keeps the alert off the card (Pick, before the end of the pick path) while the
 * shipment part still shows.
 */
export function CombineShipmentStep({
  step,
  variant,
  showAlert = true,
  onOpenOrder
}: {
  step: CombineStep;
  variant: CombineVariant;
  showAlert?: boolean;
  onOpenOrder: (order: CombineOrder) => void;
}) {
  const context =
    showAlert || !step.context ? step.context : { ...step.context, pickingOrders: [] };
  return (
    <>
      <CombineShipmentCard
        variant={variant}
        context={context}
        busy={!!step.busy}
        onScan={step.openScan}
        onCombineOrder={step.combineOrder}
        onOpenOrder={onOpenOrder}
        onRemove={step.removeOrder}
      />
      {step.sheetOpen && !step.scanOpen && (
        <CombineAlertSheet
          variant={variant}
          context={step.context}
          onScan={step.openScan}
          onDismiss={step.acknowledge}
        />
      )}
      {step.scanOpen && (
        <CombineScanOverlay
          expected={combineAlertOrders(step.context)}
          clientName={step.context?.clientName}
          onCombine={step.combineCode}
          onClose={step.closeScan}
        />
      )}
    </>
  );
}

export default CombineShipmentStep;
